import { useState } from "react";
import { ExternalLink, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";

const ProofImageCard = ({ image, index }) => {
  const [hasError, setHasError] = useState(false);
  const imageHref = image.href || image.src;

  return (
    <div className="relative w-36">
      {!hasError ? (
        <a
          href={imageHref}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-xl border border-border bg-muted/30 transition hover:border-primary/40 hover:shadow-sm"
        >
          <AspectRatio ratio={1}>
            <img
              src={image.src}
              alt={image.alt || `Foto bukti ${index + 1}`}
              className="h-full w-full object-contain p-2"
              onError={() => setHasError(true)}
            />
          </AspectRatio>
          <div className="flex items-center justify-between border-t bg-background/90 px-3 py-2 text-xs text-muted-foreground">
            <span>{image.label || `Foto ${index + 1}`}</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </div>
        </a>
      ) : (
        <div className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/80 shadow-sm">
          <AspectRatio ratio={1}>
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center text-xs text-amber-900">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <span>Foto tidak tersedia</span>
              <span className="text-[11px] text-amber-700">File hilang atau belum tersinkron</span>
            </div>
          </AspectRatio>
          <div className="border-t bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
            {image.label || `Foto ${index + 1}`}
          </div>
        </div>
      )}

      {typeof image.onRemove === "function" ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="absolute right-2 top-2 h-8 w-8 rounded-full shadow-sm"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            image.onRemove();
          }}
        >
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">Hapus foto</span>
        </Button>
      ) : null}
    </div>
  );
};

const ProofImageGallery = ({
  images = [],
  title,
  hint,
  emptyText,
  className,
}) => {
  const validImages = images.filter((image) => image && image.src);

  if (validImages.length === 0) {
    return emptyText ? <p className="text-sm text-muted-foreground">{emptyText}</p> : null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {title ? <p className="text-sm font-medium text-foreground">{title}</p> : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <div className="flex flex-wrap gap-3">
        {validImages.map((image, index) => (
          <ProofImageCard key={image.id || image.href || image.src || `${index}`} image={image} index={index} />
        ))}
      </div>
    </div>
  );
};

export default ProofImageGallery;
